import React, { useState, useEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import api from '../services/api';
import { formatExchangeName } from '../utils/exchangeDisplay';
import './AssetHistory.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
  Filler
);

// タブ定義: /trading/pnl-history の series の日次キーと累積キーに対応
const TABS = [
  { key: 'funding', cumKey: 'fundingCum', label: 'FR収益' },
  { key: 'trade', cumKey: 'tradeCum', label: '取引損益' },
  { key: 'total', cumKey: 'totalCum', label: '合計' }
];

const AssetHistory = () => {
  const [pnl, setPnl] = useState(null);
  const [currentEquity, setCurrentEquity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExchange, setSelectedExchange] = useState('');
  const [exchanges, setExchanges] = useState([]);
  const [days, setDays] = useState(30);
  const [activeTab, setActiveTab] = useState('funding');

  useEffect(() => {
    loadExchanges();
  }, []);

  useEffect(() => {
    if (selectedExchange) fetchHistory();
  }, [selectedExchange, days]);

  const loadExchanges = async () => {
    try {
      const res = await api.get('/apikey');
      const keys = res.data.apiKeys || [];
      setExchanges(keys.map(k => k.exchange));
      // 「全取引所」をデフォルト選択
      setSelectedExchange('all');
    } catch (error) {
      console.error('Load exchanges error:', error);
    }
  };

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        exchange: selectedExchange,
        days: days
      });
      // 総資産はスナップショットの最新値、損益は pnl-history から取得する
      const [pnlRes, assetRes] = await Promise.all([
        api.get(`/trading/pnl-history?${params}`),
        api.get(`/trading/asset-history?${params}`)
      ]);
      setPnl(pnlRes.data);
      const history = assetRes.data.history || [];
      setCurrentEquity(history.length > 0 ? history[history.length - 1].totalEquity : null);
    } catch (error) {
      console.error('Fetch history error:', error);
      setPnl(null);
    } finally {
      setLoading(false);
    }
  };

  const series = pnl?.series || [];
  const tab = TABS.find(t => t.key === activeTab);

  const chartData = {
    labels: series.map(d => {
      const [, month, day] = d.date.split('-');
      return `${parseInt(month)}/${parseInt(day)}`;
    }),
    datasets: [
      {
        type: 'line',
        label: `${tab.label}（累積）`,
        data: series.map(d => d[tab.cumKey]),
        borderColor: '#f7931a',
        backgroundColor: 'rgba(247, 147, 26, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      },
      {
        type: 'bar',
        label: `${tab.label}（日次）`,
        data: series.map(d => d[tab.key]),
        // 関数で指定すると凡例は値なし (=0 扱い) で緑になり、先頭バーの色に引きずられない
        backgroundColor: (ctx) => (ctx.raw ?? 0) >= 0 ? 'rgba(22, 199, 132, 0.6)' : 'rgba(234, 57, 67, 0.6)'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#a0a0a0'
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#16213e',
        titleColor: '#e0e0e0',
        bodyColor: '#e0e0e0',
        borderColor: '#2a2a4a',
        borderWidth: 1,
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatSigned(ctx.parsed.y)} USD`
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(42, 42, 74, 0.5)'
        },
        ticks: {
          color: '#a0a0a0'
        }
      },
      y: {
        grid: {
          color: 'rgba(42, 42, 74, 0.5)'
        },
        ticks: {
          color: '#a0a0a0'
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const formatNumber = (num, decimals = 8) => {
    if (num === undefined || num === null) return '-';
    return parseFloat(num).toFixed(decimals);
  };

  // 損益は 1 USD 未満になることが多いので小数 4 桁で表示する
  const formatSigned = (num) => {
    if (num === undefined || num === null) return '-';
    return `${num >= 0 ? '+' : ''}${formatNumber(num, 4)}`;
  };

  return (
    <div className="asset-history">
      <div className="page-header">
        <h1>資産履歴</h1>
        <div className="header-actions">
          <select
            value={selectedExchange}
            onChange={(e) => setSelectedExchange(e.target.value)}
            className="filter-select"
          >
            <option value="all">全取引所</option>
            {exchanges.map(ex => (
              <option key={ex} value={ex}>
                {formatExchangeName(ex)}
              </option>
            ))}
          </select>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="filter-select"
          >
            <option value={7}>7日間</option>
            <option value={30}>30日間</option>
            <option value={90}>90日間</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">読み込み中...</div>
      ) : pnl ? (
        <>
          {/* サマリー */}
          <div className="summary-grid">
            <div className="summary-card">
              <span className="summary-label">現在の総資産</span>
              <span className="summary-value">
                {formatNumber(currentEquity, 2)} USD
              </span>
            </div>
            {TABS.map(t => (
              <div className="summary-card" key={t.key}>
                <span className="summary-label">{t.label}（期間内）</span>
                <span className={`summary-value ${pnl.summary[t.key] >= 0 ? 'profit' : 'loss'}`}>
                  {formatSigned(pnl.summary[t.key])} USD
                </span>
              </div>
            ))}
          </div>

          {/* チャート */}
          <div className="chart-container">
            <h2 className="section-title">損益推移</h2>
            <div className="pnl-tabs">
              {TABS.map(t => (
                <button
                  key={t.key}
                  className={`pnl-tab ${activeTab === t.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="chart-wrapper">
              <Chart type="bar" data={chartData} options={chartOptions} />
            </div>
            <ul className="pnl-notes">
              <li>FR収益: ファンディングレートの受取額（支払いはマイナス）</li>
              <li>取引損益: 決済済みの確定損益と保有中ポジションの評価損益の合計（現物と先物を合算、手数料込み）</li>
              <li>合計: FR収益と取引損益の合計。入出金は含みません</li>
            </ul>
          </div>
        </>
      ) : (
        <div className="empty-message">損益データを取得できませんでした</div>
      )}
    </div>
  );
};

export default AssetHistory;
